import { Alert, Card } from "react-bootstrap";
import type {
    IRegDataComparisonResult,
    IRegDataStagedChange,
} from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { showToastSuccess, showToastWarning } from "../../../utils";
import type { ReconciliationGridCardModel, ReconciliationGridRow } from "./reconciliationGridTypes";
import { RegDataChangeTypes } from "./regDataAlignmentDisplay";
import {
    addStagedChange,
    addStagedChanges,
    removeStagedChanges,
    stageFromReconciliationRow,
    stageSoftDeleteFromGridRow,
} from "./regDataAlignmentState";
import {
    buildBulkSoftDeleteChanges,
    buildBulkStageSyncChanges,
    collectStagedDeleteChangeKeys,
    collectStagedSyncChangeKeys,
} from "./reconciliationBulkFilters";
import { ReconciliationBulkActions } from "./ReconciliationBulkActions";
import { ReconciliationCardSummaryBadges } from "./ReconciliationCardSummaryBadges";
import { ReconciliationGrid } from "./ReconciliationGrid";

export interface RegDataComparisonCardProps {
    card: ReconciliationGridCardModel;
    comparison: IRegDataComparisonResult | null;
    stagedChanges: IRegDataStagedChange[];
    onStagedChangesChange: (changes: IRegDataStagedChange[]) => void;
    onEditedValueChange: (changeKey: string, editedValue: Record<string, unknown>) => void;
    onUndoStaged: (changeKey: string) => void;
}

export function RegDataComparisonCard({
    card,
    comparison,
    stagedChanges,
    onStagedChangesChange,
    onEditedValueChange,
    onUndoStaged,
}: RegDataComparisonCardProps) {
    const handleStageRow = (row: ReconciliationGridRow) => {
        if (row.stageChangeType === RegDataChangeTypes.SoftDeleteExisting) {
            const change = stageSoftDeleteFromGridRow(row);
            if (!change) return;
            onStagedChangesChange(addStagedChange(stagedChanges, change));
            return;
        }

        const change = stageFromReconciliationRow(row);
        if (!change) return;
        onStagedChangesChange(addStagedChange(stagedChanges, change));
    };

    const handleBulkStageSync = () => {
        const { changes, stagedCount, skippedIncompleteCount } = buildBulkStageSyncChanges(card.gridRows);
        if (stagedCount === 0 && skippedIncompleteCount === 0) return;

        onStagedChangesChange(addStagedChanges(stagedChanges, changes));

        if (stagedCount > 0 && skippedIncompleteCount > 0) {
            showToastSuccess(
                `${stagedCount} pending, skipped ${skippedIncompleteCount} needing details (${card.label}).`
            );
        } else if (stagedCount > 0) {
            showToastSuccess(`${stagedCount} pending change${stagedCount === 1 ? "" : "s"} (${card.label}).`);
        } else if (skippedIncompleteCount > 0) {
            showToastWarning(
                `Skipped ${skippedIncompleteCount} row${skippedIncompleteCount === 1 ? "" : "s"} needing details (${card.label}).`
            );
        }
    };

    const handleBulkRevertSync = () => {
        const keys = collectStagedSyncChangeKeys(card.gridRows);
        if (keys.length === 0) return;
        onStagedChangesChange(removeStagedChanges(stagedChanges, keys));
        showToastSuccess(`Reverted ${keys.length} pending sync${keys.length === 1 ? "" : "s"} (${card.label}).`);
    };

    const handleBulkSoftDelete = () => {
        const changes = buildBulkSoftDeleteChanges(card.gridRows);
        if (changes.length === 0) return;
        onStagedChangesChange(addStagedChanges(stagedChanges, changes));
        showToastSuccess(
            `Marked ${changes.length} record${changes.length === 1 ? "" : "s"} for soft-delete (${card.label}).`
        );
    };

    const handleBulkRestoreDelete = () => {
        const keys = collectStagedDeleteChangeKeys(card.gridRows);
        if (keys.length === 0) return;
        onStagedChangesChange(removeStagedChanges(stagedChanges, keys));
        showToastSuccess(`Restored ${keys.length} pending delete${keys.length === 1 ? "" : "s"} (${card.label}).`);
    };

    return (
        <Card className="mb-3">
            <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <span>{card.label}</span>
                <ReconciliationCardSummaryBadges summary={card.summary} />
            </Card.Header>
            <Card.Body>
                {card.fieldLifecycleNote && (
                    <Alert variant="info" className="py-2 small">
                        {card.fieldLifecycleNote}
                    </Alert>
                )}

                {card.warnings.length > 0 && (
                    <Alert variant="warning" className="py-2 small">
                        {card.warnings.map((warning, index) => (
                            <div key={`${warning.code}-${index}`}>
                                <strong>{warning.code}</strong>: {warning.message}
                            </div>
                        ))}
                    </Alert>
                )}

                <ReconciliationBulkActions
                    gridRows={card.gridRows}
                    onBulkStageSync={handleBulkStageSync}
                    onBulkRevertSync={handleBulkRevertSync}
                    onBulkSoftDelete={handleBulkSoftDelete}
                    onBulkRestoreDelete={handleBulkRestoreDelete}
                />

                <ReconciliationGrid
                    card={card}
                    comparison={comparison}
                    onStageRow={handleStageRow}
                    onUndoStaged={onUndoStaged}
                    onEditedValueChange={onEditedValueChange}
                />
            </Card.Body>
        </Card>
    );
}
